import React from 'react'
import {Tabs as ChakraTabs, Tab, TabList, TabPanel, TabPanels, TabsProps as ChakraTabsProps, Skeleton} from '@chakra-ui/react'

interface Tab {
    label: string
    isDisabled?: boolean
    content?: React.ReactNode
}

interface TabsProps extends Omit<ChakraTabsProps, 'children'> {
    /**
     * Array of objects representing the tabs.
     * Each object should have a `label` property for the tab text and an optional `content` property for the tab content.
     */
    tabs: Array<Tab>
    /**
     * Index of the active tab.
     */
    activeIndex?: number
    /**
     * Function to handle tab change.
     */
    onTabChange?: (index: number) => void
    /**
     * Enables default behaviour of tabs, showing content based on `tabs` object value.
     * Enable this when you want to show something under the tabs.
     */
    enableDefaultPanels?: boolean
    variant?: 'recommendations' | 'sizeGuide'
}

/**
 * @name Tabs
 *
 * @description
 * Renders custom tabs using Chakra UI default Tabs.
 * Allows creating custom tabs by passing an array of objects.
 *
 * @component
 * @param {TabsProps} props - Component properties.
 * @param {Array<CustomTab>} props.tabs - Array of objects representing the tabs.
 * @param {number} props.activeIndex - Index of the active tab.
 * @param {boolean} [props.enableDefaultPanels] - Decides if tabs has to render tab panels children under the tabs list.
 * @param {function} props.onTabChange - Function to handle tab change.
 * @param {TabsProps} props.rest - Additional properties passed to Chakra UI Tabs.
 *
 * @returns {JSX.Element} - Rendered custom tabs component.
 */
const Tabs = ({tabs, activeIndex, onTabChange, enableDefaultPanels = false, variant, ...props}: TabsProps): JSX.Element => {
    return (
        <ChakraTabs variant={variant} isManual defaultIndex={activeIndex} onChange={onTabChange} {...props}>
            <TabList>
                {tabs.map((tab, index) => (
                    <Tab key={index} isDisabled={tab.isDisabled}>
                        {tab.label}
                    </Tab>
                ))}
            </TabList>
            {enableDefaultPanels && (
                <TabPanels>
                    {tabs.map((tab, index) => (
                        <TabPanel key={index}>{tab.content}</TabPanel>
                    ))}
                </TabPanels>
            )}
        </ChakraTabs>
    )
}

interface TabsSkeletonProps {
    tabsCount: number
    enableDefaultPanels?: boolean
}

/**
 * @name TabsSkeleton
 *
 * @description
 * Displays skeleton loading indicators for tabs and panels.
 *
 * @component
 * @param {TabsSkeletonProps} props - The props for the TabsSkeleton component.
 * @param {number} props.tabCount - Number of tabs
 * @param {boolean} [props.enableDefaultPanels] - Enables rendering of panels.
 *
 * @returns {JSX.Element} The rendered TabsSkeleton component.
 */
const TabsSkeleton = ({tabsCount, enableDefaultPanels = false}: TabsSkeletonProps): JSX.Element => {
    return (
        <ChakraTabs data-testid="tabs-skeleton">
            <TabList>
                {Array.from({length: tabsCount}).map((_, index) => (
                    <Tab key={index} paddingInline={0} isDisabled>
                        <Skeleton height="14px" width="120px" py="spacing.3" />
                    </Tab>
                ))}
            </TabList>
            {enableDefaultPanels && (
                <TabPanels>
                    {Array.from({length: tabsCount}).map((_, index) => (
                        <TabPanel key={index}>
                            <Skeleton height="100px" />
                        </TabPanel>
                    ))}
                </TabPanels>
            )}
        </ChakraTabs>
    )
}

export {Tabs, TabsSkeleton, TabsProps}
